import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Keyboard,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import ShieldIcon from "../../../../../../../../assets/images/Shield.svg";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import countryList from "../../../../../../../../apps/quickstage-bff-web/src/services/countryList.json";
import {
  searchLocationSuggestions,
  getPlaceDetails,
} from "../../../../../services/location.service";
import {
  mapiSenderAddressFieldDisplayOrder,
  mapiSenderAddressFields,
} from "../../../../../constants/send.constant";
import {
  getFieldLabel,
  getFieldMax,
  getFieldMin,
  validateField,
} from "../../../../../helper/fieldHelper";
import {
  normalizeUsCountrySubdivisionCode,
  stripUsCountrySubdivisionPrefix,
} from "../../../../../helper/mapiSubdivisionCode";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface MapiSenderAddressStepHandle {
  submit: () => Promise<boolean>;
}

const LINE1_TAG = mapiSenderAddressFields.senderAddressline1;
const SUBDIVISION_TAG = mapiSenderAddressFields.senderState;
const COUNTRY_CODE_TAG = mapiSenderAddressFields.senderCountryCode;

/** Subdivision-like segments from location place payloads (Amazon Location / similar). */
function pickSubdivisionRegionFromPlace(place: any): string {
  const keys = ["Region", "SubRegion", "AdministrativeArea"] as const;
  for (const k of keys) {
    const v = place?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

/**
 * When `countryList` defines `States` for `countryCode`, map region (name or abbr) to abbreviation;
 * otherwise return `region` unchanged (same idea as passing through Municipality for city).
 */
function subdivisionCodeFromCountryList(
  region: string,
  countryCode: string
): string {
  const r = (region || "").trim();
  if (!r) return "";
  const cc = (countryCode || "").trim();
  if (!cc) return r;
  const country = (countryList as any[]).find(
    (c: any) => c?.CountryCode === cc
  );
  const states = country?.States;
  if (!Array.isArray(states) || states.length === 0) return r;
  const byName = states.find(
    (s: any) => (s.stateName || "").toLowerCase() === r.toLowerCase()
  );
  if (byName?.stateAbbrivation)
    return String(byName.stateAbbrivation).trim();
  const byAbbr = states.find(
    (s: any) =>
      (s.stateAbbrivation || "").toUpperCase() === r.toUpperCase()
  );
  if (byAbbr?.stateAbbrivation)
    return String(byAbbr.stateAbbrivation).trim();
  return r;
}

const MapiSenderAddressStep = forwardRef<
  MapiSenderAddressStepHandle,
  Props
>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const transactionFieldsData = useSessionStore(
    (s: SessionState) => (s as any).mapiTransactionFieldsData
  );
  const fieldInfo: any[] = transactionFieldsData?.fieldInfo ?? [];

  const visibleTags = useMemo(() => {
    return mapiSenderAddressFieldDisplayOrder.filter((tag) =>
      fieldInfo.some((f: any) => f.field === tag)
    );
  }, [fieldInfo]);

  const mapiSenderAddressValues =
    useSessionStore(
      (s: SessionState) => (s as any).mapiSenderAddressValues
    ) ?? {};

  const setMapiSenderAddressField = useSessionStore(
    (s: SessionState) => (s as any).setMapiSenderAddressField
  );

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);

  const readValue = useCallback(
    (tag: string): string => {
      const raw = mapiSenderAddressValues[tag];
      return typeof raw === "string" ? raw : "";
    },
    [mapiSenderAddressValues]
  );

  const writeValue = useCallback(
    (tag: string, text: string) => {
      setMapiSenderAddressField?.(tag, text);
    },
    [setMapiSenderAddressField]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const usaStates = useMemo(() => {
    try {
      const usa = (countryList as any[]).find(
        (c: any) => c?.CountryCode === "USA"
      );
      return (usa?.States ?? []) as Array<{
        stateid: number;
        stateAbbrivation: string;
        stateName: string;
      }>;
    } catch {
      return [] as Array<{
        stateid: number;
        stateAbbrivation: string;
        stateName: string;
      }>;
    }
  }, []);

  const showSubdivisionPicker = visibleTags.includes(SUBDIVISION_TAG);
  const stateQuery = useMemo(
    () =>
      stripUsCountrySubdivisionPrefix(readValue(SUBDIVISION_TAG)).trim().toUpperCase(),
    [readValue]
  );
  const isExactAbbreviation = useMemo(() => {
    return usaStates.some(
      (s) =>
        (s.stateAbbrivation || "").toUpperCase() === stateQuery &&
        stateQuery.length > 0
    );
  }, [usaStates, stateQuery]);

  const filteredStates = useMemo(() => {
    if (!stateQuery) return usaStates;
    return usaStates.filter(
      (s) =>
        (s.stateName || "").toUpperCase().includes(stateQuery) ||
        (s.stateAbbrivation || "").toUpperCase().includes(stateQuery)
    );
  }, [usaStates, stateQuery]);

  const isValid = useMemo(() => {
    if (visibleTags.length === 0) return true;
    return visibleTags.every(
      (tag) => validateField(fieldInfo, tag, readValue(tag)) === ""
    );
  }, [visibleTags, fieldInfo, readValue]);

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        const newErrors: Record<string, string> = {};
        for (const tag of visibleTags) {
          newErrors[tag] = validateField(fieldInfo, tag, readValue(tag));
        }
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
      },
    }),
    [visibleTags, fieldInfo, readValue]
  );

  const handleAddressChange = (text: string) => {
    setMapiSenderAddressField?.(LINE1_TAG, text);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (text.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceTimerRef.current = setTimeout(async () => {
      const currentRequestId = ++requestIdRef.current;
      try {
        const results = await searchLocationSuggestions(text);
        if (currentRequestId !== requestIdRef.current) return;
        setSuggestions(results || []);
        setShowSuggestions(!!results?.length);
      } catch (e) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Location suggestion error", e);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleSelectSuggestion = async (item: any) => {
    try {
      const place = await getPlaceDetails(item?.PlaceId);
      if (visibleTags.includes(LINE1_TAG)) {
        const line1 =
          `${place?.AddressNumber ?? ""} ${place?.Street ?? ""}`.trim() ||
          place?.Label?.slice(0, 30) ||
          "";
        const maxL1 = getFieldMax(fieldInfo, LINE1_TAG)
          ? Number(getFieldMax(fieldInfo, LINE1_TAG))
          : undefined;
        const line1Val = maxL1 && line1 ? line1.slice(0, maxL1) : line1;
        setMapiSenderAddressField?.(LINE1_TAG, line1Val);
      }
      if (visibleTags.includes(mapiSenderAddressFields.senderCity)) {
        const cityRaw =
          place?.Municipality?.slice(0, 20) ||
          (place?.Neighborhood || "").slice(0, 20) ||
          "";
        const maxC = getFieldMax(fieldInfo, mapiSenderAddressFields.senderCity)
          ? Number(getFieldMax(fieldInfo, mapiSenderAddressFields.senderCity))
          : undefined;
        const cityVal = maxC && cityRaw ? cityRaw.slice(0, maxC) : cityRaw;
        setMapiSenderAddressField?.(mapiSenderAddressFields.senderCity, cityVal);
      }
      if (visibleTags.includes(mapiSenderAddressFields.senderPostalCode)) {
        const pc = place?.PostalCode?.slice(0, 10) || "";
        const maxP = getFieldMax(
          fieldInfo,
          mapiSenderAddressFields.senderPostalCode
        )
          ? Number(
              getFieldMax(fieldInfo, mapiSenderAddressFields.senderPostalCode)
            )
          : undefined;
        const pcVal = maxP && pc ? pc.slice(0, maxP) : pc;
        setMapiSenderAddressField?.(
          mapiSenderAddressFields.senderPostalCode,
          pcVal
        );
      }
      if (visibleTags.includes(SUBDIVISION_TAG)) {
        const rawRegion = pickSubdivisionRegionFromPlace(place);
        const countryFromPlace = (place?.Country || "").trim();
        const resolved = subdivisionCodeFromCountryList(
          rawRegion,
          countryFromPlace
        );
        const combined = (resolved || rawRegion).trim();
        const maxSub = getFieldMax(fieldInfo, SUBDIVISION_TAG)
          ? Number(getFieldMax(fieldInfo, SUBDIVISION_TAG))
          : undefined;
        const subVal =
          maxSub && combined ? combined.slice(0, maxSub) : combined;
        if (subVal) {
          setMapiSenderAddressField?.(
            SUBDIVISION_TAG,
            normalizeUsCountrySubdivisionCode(subVal)
          );
        }
      }
      if (visibleTags.includes(COUNTRY_CODE_TAG)) {
        const cc = (place?.Country || "").trim();
        if (cc) {
          const maxC = getFieldMax(fieldInfo, COUNTRY_CODE_TAG)
            ? Number(getFieldMax(fieldInfo, COUNTRY_CODE_TAG))
            : undefined;
          const countryVal = maxC ? cc.slice(0, maxC) : cc;
          setMapiSenderAddressField?.(COUNTRY_CODE_TAG, countryVal);
        }
      }
    } catch (e) {
      console.error("GetPlace error", e);
    }
    setShowSuggestions(false);
    Keyboard.dismiss();
  };

  const clearFieldError = (tag: string) => {
    setErrors((prev) => {
      if (!prev[tag]) return prev;
      const next = { ...prev };
      delete next[tag];
      return next;
    });
  };

  const fallbackLabel = (tag: string) => {
    if (tag === LINE1_TAG) return t("ADDRESS");
    if (tag === mapiSenderAddressFields.senderCity)
      return t("SENDER_ADDRESS_CITY_PLACEHOLDER");
    if (tag === mapiSenderAddressFields.senderPostalCode)
      return t("SENDER_ADDRESS_POSTAL_PLACEHOLDER");
    if (tag === SUBDIVISION_TAG)
      return t("SENDER_ADDRESS_STATE_PLACEHOLDER");
    return tag;
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("SENDER_ADDRESS_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_ADDRESS_SUBTITLE")}</Text>
      <View>
        {visibleTags.map((tag, idx) => {
          const isFirst = idx === 0;
          const maxLen = getFieldMax(fieldInfo, tag);
          const minLen = getFieldMin(fieldInfo, tag);
          const maxLength = maxLen ? Number(maxLen) : undefined;
          const minLength = minLen ? Number(minLen) : undefined;
          const label = getFieldLabel(fieldInfo, tag) || fallbackLabel(tag);
          const testIdSuffix = tag.replace(/\./g, "-");

          if (tag === LINE1_TAG) {
            return (
              <React.Fragment key={tag}>
                <FloatingTextInputComponent
                  testID={`mapi-senderAddress-${testIdSuffix}`}
                  label={label}
                  value={readValue(LINE1_TAG)}
                  isAutoFocus={isFirst}
                  maxLength={maxLength}
                  minLength={minLength}
                  onChangeText={(text) => {
                    handleAddressChange(text);
                    clearFieldError(tag);
                  }}
                  errorText={errors[tag]}
                />
                {showSuggestions && (
                  <View style={styles.dropdownContainer}>
                    <FlatList
                      keyboardShouldPersistTaps="handled"
                      data={suggestions}
                      keyExtractor={(item) => item?.PlaceId}
                      renderItem={({ item }) => (
                        <TouchableOpacity
                          style={styles.suggestionItem}
                          onPress={() => handleSelectSuggestion(item)}
                        >
                          <Text style={styles.suggestionText}>{item.Text}</Text>
                        </TouchableOpacity>
                      )}
                    />
                  </View>
                )}
              </React.Fragment>
            );
          }

          if (tag === SUBDIVISION_TAG) {
            return (
              <React.Fragment key={tag}>
                <FloatingTextInputComponent
                  testID={`mapi-senderAddress-${testIdSuffix}`}
                  label={label}
                  value={readValue(SUBDIVISION_TAG)}
                  isAutoFocus={isFirst}
                  maxLength={maxLength}
                  minLength={minLength}
                  onChangeText={(v) => {
                    setMapiSenderAddressField?.(
                      SUBDIVISION_TAG,
                      normalizeUsCountrySubdivisionCode(v)
                    );
                    clearFieldError(tag);
                  }}
                  errorText={errors[tag]}
                />
              </React.Fragment>
            );
          }

          const val = readValue(tag);
          return (
            <FloatingTextInputComponent
              key={tag}
              testID={`mapi-senderAddress-${testIdSuffix}`}
              label={label}
              value={val}
              isAutoFocus={isFirst}
              maxLength={maxLength}
              minLength={minLength}
              onChangeText={(v) => {
                writeValue(tag, v);
                clearFieldError(tag);
              }}
              errorText={errors[tag]}
            />
          );
        })}

        {showSubdivisionPicker &&
          !isExactAbbreviation &&
          stateQuery.length > 0 &&
          filteredStates.length > 0 && (
            <View style={styles.dropdownContainer}>
              <FlatList
                keyboardShouldPersistTaps="handled"
                data={filteredStates}
                keyExtractor={(item) => String(item.stateid)}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    testID="mapi-senderStateOption"
                    style={styles.suggestionItem}
                    onPress={() => {
                      try {
                        setMapiSenderAddressField?.(
                          SUBDIVISION_TAG,
                          normalizeUsCountrySubdivisionCode(
                            item.stateAbbrivation
                          )
                        );
                      } catch {
                        /* ignore */
                      }
                      setShowSuggestions(false);
                      Keyboard.dismiss();
                    }}
                  >
                    <Text style={styles.suggestionText}>
                      {item.stateName} ({item.stateAbbrivation})
                    </Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}
      </View>
      <View style={styles.hintBox}>
        <ShieldIcon width={24} height={24} />
        <Text style={styles.hintText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
    </View>
  );
});

export default MapiSenderAddressStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: Colors.borderGrey,
    backgroundColor: Colors.white,
  },
  suggestionText: {
    fontSize: 16,
    fontFamily: ComponentStyles.textFamily,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
  },
  dropdownContainer: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: Colors.textInputBackground,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 32,
    backgroundColor: Colors.white,
    marginBottom: 79,
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16,
  },
});
